import { useState } from 'react';
import { CheckCircle2Icon, ExternalLinkIcon } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ErrorNotice, Field, ListField, Panel, Pill, SkeletonTable, TextInput, type PillTone } from '@bcecloud/portal-ui';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import { PLACEMENT_STATUS, type PlacementStatus } from '@/lib/types';

/** Review states of a partner's submitted card change (the API's REVIEW_STATUS). */
const REVIEW_STATUS = { pending: 'pending', approved: 'approved', rejected: 'rejected', needsStaff: 'needs_staff' } as const;
type ReviewStatus = (typeof REVIEW_STATUS)[keyof typeof REVIEW_STATUS];

interface ListingContent {
  readonly photoUrl?: string | null;
  readonly blurb?: string | null;
  readonly websiteUrl?: string | null;
}

/** GET /organizations/:id/partner-listing. */
export interface StaffPartnerListing {
  readonly websiteUrl: string | null;
  readonly photoUrl: string | null;
  readonly blurb: string | null;
  readonly cities: readonly string[];
  readonly published: boolean;
  readonly placement: { readonly url: string | null; readonly status: PlacementStatus; readonly found: string | null; readonly checkedAt: string | null; readonly detail: string | null };
  readonly submission: {
    readonly content: ListingContent;
    readonly submittedAt: string;
    readonly status: ReviewStatus;
    readonly reviewedBy: 'ai' | 'staff' | null;
    readonly reason: string | null;
  } | null;
  readonly limits: { readonly blurbMax: number; readonly maxCities: number };
}

const SITE = 'https://bestcorporateevents.com';
const PLACEMENT_LABEL: Record<PlacementStatus, { label: string; tone: PillTone }> = {
  [PLACEMENT_STATUS.none]: { label: 'Not checked', tone: 'neutral' },
  [PLACEMENT_STATUS.verified]: { label: 'Verified', tone: 'success' },
  [PLACEMENT_STATUS.missing]: { label: 'Missing from their page', tone: 'danger' },
  [PLACEMENT_STATUS.unreachable]: { label: 'Page unreachable', tone: 'warning' },
};

const shortDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

function ContentRows({ content, live }: { readonly content: ListingContent; readonly live?: StaffPartnerListing }): React.ReactElement {
  // A submitted change shows null as a removal; the live card shows it as empty.
  const empty = live ? '(removed)' : 'None';
  return (
    <dl className="grid gap-3 text-portal-small sm:grid-cols-[140px_minmax(0,1fr)]">
      {content.websiteUrl !== undefined ? (
        <>
          <dt className="text-portal-muted">Website</dt>
          <dd>
            {content.websiteUrl ?? empty}
            {live ? <span className="text-portal-muted"> (was {live.websiteUrl ?? 'empty'})</span> : null}
          </dd>
        </>
      ) : null}
      {content.blurb !== undefined ? (
        <>
          <dt className="text-portal-muted">Description</dt>
          <dd>{content.blurb ?? empty}</dd>
        </>
      ) : null}
      {content.photoUrl !== undefined ? (
        <>
          <dt className="text-portal-muted">Photo</dt>
          <dd>{content.photoUrl ? <img src={content.photoUrl} alt="Submitted card photo" className="aspect-[4/3] w-56 rounded-lg object-cover" /> : empty}</dd>
        </>
      ) : null}
    </dl>
  );
}

/**
 * The partner's card on our city pages. Staff choose the city pages, decide
 * changes the AI reviewer was unsure of, and can check the partner's page. The
 * card shows only while the partner's site still shows our showcase or link.
 */
export function OrgPartnerListingPanel({ organizationId, organizationName }: { readonly organizationId: string; readonly organizationName: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const key = ['partner-listing', organizationId] as const;
  const listing = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => getJson<StaffPartnerListing>(`/organizations/${organizationId}/partner-listing`, undefined, signal),
  });
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => sendJson<StaffPartnerListing>('patch', `/organizations/${organizationId}/partner-listing`, body),
    onSuccess: (data) => queryClient.setQueryData(key, data),
  });
  const check = useMutation({
    mutationFn: () => sendJson<StaffPartnerListing>('post', `/organizations/${organizationId}/partner-listing/check`, {}),
    onSuccess: (data) => queryClient.setQueryData(key, data),
  });

  if (listing.isPending) return <SkeletonTable rows={3} columns={['w-[30%]', 'w-[70%]']} label="Loading the city page card…" />;
  if (listing.isError) return <ErrorNotice title="City page card unavailable" message={errorMessage(listing.error)} />;
  return (
    <ListingEditor
      key={`${listing.data.cities.join()}|${listing.data.placement.url ?? ''}|${listing.data.submission?.status ?? ''}`}
      organizationName={organizationName}
      listing={listing.data}
      saving={save.isPending || check.isPending}
      error={save.isError ? errorMessage(save.error) : check.isError ? errorMessage(check.error) : undefined}
      onSave={(body) => save.mutate(body)}
      onCheck={() => check.mutate()}
    />
  );
}

interface EditorProps {
  readonly organizationName: string;
  readonly listing: StaffPartnerListing;
  readonly saving: boolean;
  readonly error: string | undefined;
  readonly onSave: (body: Record<string, unknown>) => void;
  readonly onCheck: () => void;
}

export function ListingEditor({ organizationName, listing, saving, error, onSave, onCheck }: EditorProps): React.ReactElement {
  const [cities, setCities] = useState<string[]>([...listing.cities]);
  const [placementUrl, setPlacementUrl] = useState(listing.placement.url ?? '');
  const [reason, setReason] = useState('');
  const waiting = listing.submission && (listing.submission.status === REVIEW_STATUS.pending || listing.submission.status === REVIEW_STATUS.needsStaff) ? listing.submission : null;
  const citiesDirty = cities.join('\n') !== listing.cities.join('\n');
  const placementDirty = placementUrl.trim() !== (listing.placement.url ?? '');
  const placement = PLACEMENT_LABEL[listing.placement.status];

  return (
    <div className="mt-8 space-y-8">
      <div>
        <h2 className="text-portal-section font-bold tracking-[-0.4px]">City page card</h2>
        <p className="mt-1 text-portal-small text-portal-muted">
          {organizationName}&rsquo;s card on our city pages, with a followed link to their site. It shows while their site shows our showcase or links to us;
          the partner edits the content, and the AI reviewer passes each change or sends it here.
        </p>
      </div>

      {error ? <ErrorNotice inline title="Could not save" message={error} /> : null}

      <Panel className="p-5">
        {listing.published ? (
          <div className="flex flex-wrap items-center gap-3">
            <CheckCircle2Icon aria-hidden className="size-5 text-emerald-600" />
            <span className="text-portal-small font-bold">Live on</span>
            {listing.cities.map((slug) => (
              <a key={slug} href={`${SITE}/featured-locations/${slug}`} target="_blank" rel="noopener" className="inline-flex items-center gap-1 text-portal-small font-bold text-brand hover:underline">
                {slug}
                <ExternalLinkIcon aria-hidden className="size-3.5" />
              </a>
            ))}
          </div>
        ) : (
          <p className="text-portal-small">
            <span className="font-bold">Not showing.</span>{' '}
            <span className="text-portal-muted">
              {[
                listing.cities.length ? null : 'No city page set.',
                listing.websiteUrl ? null : 'No website.',
                listing.placement.status === PLACEMENT_STATUS.verified ? null : 'Their page is not verified.',
              ]
                .filter(Boolean)
                .join(' ')}
            </span>
          </p>
        )}
      </Panel>

      {waiting ? (
        <div>
          <h3 className="mb-3 text-portal-body font-bold">Change waiting for review</h3>
          <Panel className="space-y-4 p-5">
            <p className="text-portal-small">
              <Pill tone="info">{waiting.status === REVIEW_STATUS.needsStaff ? 'Sent to staff by the AI' : 'AI review running'}</Pill>{' '}
              <span className="text-portal-muted">Submitted {shortDate(waiting.submittedAt)}.</span>
              {waiting.reason ? <span className="text-portal-muted"> AI: {waiting.reason}</span> : null}
            </p>
            <ContentRows content={waiting.content} live={listing} />
            <div className="flex flex-wrap items-end gap-3">
              <Button variant="primary" disabled={saving} onClick={() => onSave({ decision: { approve: true } })}>
                Approve
              </Button>
              <Field label="Reason, if declining" htmlFor="decline-reason" className="min-w-[280px] flex-1">
                <TextInput id="decline-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What the partner should change" />
              </Field>
              <Button variant="secondary" disabled={saving || !reason.trim()} onClick={() => onSave({ decision: { approve: false, reason: reason.trim() } })}>
                Decline
              </Button>
            </div>
          </Panel>
        </div>
      ) : null}

      <div className="grid gap-8 xl:grid-cols-2">
        <div>
          <h3 className="mb-3 text-portal-body font-bold">City pages</h3>
          <Panel className="space-y-4 p-5">
            <ListField
              label="City page slugs"
              description={`One per line, as in the page address: boston-ma for /featured-locations/boston-ma. Up to ${listing.limits.maxCities}.`}
              value={cities}
              placeholder="boston-ma"
              onChange={setCities}
            />
            <Button variant="primary" disabled={!citiesDirty || saving} onClick={() => onSave({ cities })}>
              Save city pages
            </Button>
          </Panel>
        </div>

        <div>
          <h3 className="mb-3 flex items-center gap-2 text-portal-body font-bold">
            Their page <Pill tone={placement.tone}>{placement.label}</Pill>
          </h3>
          <Panel className="space-y-4 p-5">
            <Field
              label="Page on their site"
              htmlFor="placement-url"
              hint={listing.placement.checkedAt ? `Checked ${shortDate(listing.placement.checkedAt)}${listing.placement.found ? `: found our ${listing.placement.found}` : ''}. Rechecked every Monday.` : 'Checked when saved, then every Monday.'}
            >
              <TextInput id="placement-url" type="url" value={placementUrl} onChange={(e) => setPlacementUrl(e.target.value)} placeholder="https://theirsite.com/events" />
            </Field>
            {listing.placement.detail && listing.placement.status !== PLACEMENT_STATUS.verified ? (
              <p className="text-portal-micro text-rose-700">{listing.placement.detail}</p>
            ) : null}
            <div className="flex flex-wrap gap-3">
              <Button variant="primary" disabled={!placementDirty || saving} onClick={() => onSave({ placementUrl: placementUrl.trim() || null })}>
                Save and check
              </Button>
              <Button variant="secondary" disabled={!listing.placement.url || placementDirty || saving} onClick={onCheck}>
                Check now
              </Button>
            </div>
          </Panel>
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-portal-body font-bold">Live card</h3>
        <Panel className="p-5">
          <ContentRows content={{ websiteUrl: listing.websiteUrl, blurb: listing.blurb, photoUrl: listing.photoUrl }} />
        </Panel>
      </div>
    </div>
  );
}
